import backendCall from './AxiosService';
import type { Photo, PhotoUploadResponse, PhotoListResponse } from '@/types/photos';

export class PhotoService {
  /**
   * Upload a photo for a check
   */
  static async uploadPhotoForCheck(
    checkId: number,
    file: File,
    photoType: string = 'check_photo',
    description?: string
  ): Promise<Photo> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photoType', photoType);
    if (description) {
      formData.append('description', description);
    }

    const response = await backendCall.post<PhotoUploadResponse>(`photos/check/${checkId}/upload`, formData, {
      headers: {
        // Don't set Content-Type, let the browser set it automatically for FormData
      },
    });
    return response.data.photo;
  }

  /**
   * Upload a photo for a request
   */
  static async uploadPhotoForRequest(
    requestId: number,
    file: File,
    photoType: string = 'check_photo',
    description?: string
  ): Promise<Photo> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photoType', photoType);
    if (description) {
      formData.append('description', description);
    }

    const response = await backendCall.post<PhotoUploadResponse>(`photos/request/${requestId}/upload`, formData, {
      headers: {
        // Don't set Content-Type, let the browser set it automatically for FormData
      },
    });
    return response.data.photo;
  }

  /**
   * Get photos for a check
   */
  static async getPhotosForCheck(checkId: number): Promise<Photo[]> {
    const response = await backendCall.get<PhotoListResponse>(`photos/check/${checkId}`);
    return response.data.photos;
  }

  /**
   * Get photos for a request
   */
  static async getPhotosForRequest(requestId: number): Promise<Photo[]> {
    const response = await backendCall.get<PhotoListResponse>(`photos/request/${requestId}`);
    return response.data.photos;
  }

  /**
   * Delete a photo
   */
  static async deletePhoto(photoId: number): Promise<void> {
    await backendCall.delete(`photos/${photoId}`);
  }

  /**
   * Update photo description
   */
  static async updatePhotoDescription(photoId: number, description: string): Promise<Photo> {
    const response = await backendCall.patch<PhotoUploadResponse>(`photos/${photoId}/description`, {
      description,
    });
    return response.data.photo;
  }

  /**
   * Upload a photo for a unit (temporary, will be associated with check later)
   */
  static async uploadPhotoForUnit(
    unitNumber: string,
    file: File,
    photoType: string = 'check_photo',
    description?: string
  ): Promise<Photo> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('photoType', photoType);
    if (description) {
      formData.append('description', description);
    }

    const response = await backendCall.post<PhotoUploadResponse>(`photos/unit/${unitNumber}/upload`, formData, {
      headers: {
        // Don't set Content-Type, let the browser set it automatically for FormData
      },
    });
    return response.data.photo;
  }

  /**
   * Get photos for a unit
   */
  static async getPhotosForUnit(unitNumber: string): Promise<Photo[]> {
    const response = await backendCall.get<PhotoListResponse>(`photos/unit/${unitNumber}`);
    return response.data.photos;
  }
} 